/**
 * Pure helpers for ResolveWithValueDialog's two-question resolve flow.
 * Kept structural and side-effect free so they can be unit tested without
 * Apollo/MUI.
 */
import {
  EPA_ID_FORMAT_REASON,
  isValidEPAId,
  normalizeEpaId,
} from "encamp-compliance-engine/src/schemas/rcra/epaId";
import { getEpaFormBox } from "encamp-shared/src/waste/discrepancyFieldMeta";
import { normalizeEpaWasteUnit } from "encamp-shared/src/waste/eManifestWasteUnit";
import { invalidManifestCorrectionWasteCodes } from "encamp-shared/src/waste/manifestCorrectionWasteCodes";
import { normalizeStateWasteCodes } from "encamp-shared/src/waste/stateWasteCodeNormalization";
import { isPositiveQuantity, parseTypedQuantity } from "encamp-shared/src/waste/typedQuantity";

import {
  formatFieldValue,
  getFieldLabel,
  getDiscrepancyStatus,
  getStatusLabel,
  topSkipFromSummary,
  supportsVendorDataUpdate,
  type SkipGroup,
} from "./DiscrepancyReviewDialog.helpers";
import { type EManifestBox18a } from "./EManifestBox18aCaption";
import { copyWarningMessages } from "./resolveWithValue.copyWarnings";
import { computeDocumentEligibility } from "./resolveWithValue.documentEligibility";
import {
  isSameAsDocument,
  isWasteCodeField,
  parseWasteCodes,
  serializeWasteCodes,
  normalizeForCompare,
} from "./resolveWithValue.values";
import {
  isVendorLineAdditionRow,
  isVendorLineAdditionSave,
  vendorLineAdditionDisabledReason,
} from "./vendorLineAddition";

export {
  computeDocumentEligibility,
  DOCUMENT_WRITABLE_FIELDS,
  type DocumentEligibility,
  type DocumentEligibilityRow,
} from "./resolveWithValue.documentEligibility";
export {
  isSameAsDocument,
  isWasteCodeField,
  parseWasteCodes,
  serializeWasteCodes,
  normalizeForCompare,
} from "./resolveWithValue.values";
/**
 * The minimal row shape these helpers operate on. Fields other than
 * id/field/status are optional (not just nullable) to structurally match
 * the generated BulkManifestDiscrepanciesQuery row.
 */
export interface ResolveRow {
  id: string;
  field: string;
  status: string;
  eManifestValue?: string | null;
  vendorValue?: string | null;
  manifestDocumentValue?: string | null;
  documentDiscrepant?: boolean | null;
  vendorAlignmentSuspect?: boolean | null;
  vendorWasteLinesUnmatched?: boolean | null;
  vendorWasteLinesMatched?: boolean | null;
  wasteLineUnifiedId?: string | null;
  wasteLineNumber?: string | null;
  wasteLineDotDescription?: string | null;
  sourceDocumentId?: string | null;
  sourceDocumentFileExtension?: string | null;
  eManifestBox18a?: EManifestBox18a | null;
  /** Read by computeActionEligibility to say why a row can't be dismissed. */
  kind?: string | null;
  resolutionPath?: string | null;
  escalation?: string | null;
}

export type Q1Answer =
  | "EMANIFEST"
  | "VENDOR"
  | "DOCUMENT"
  | "SOMETHING_ELSE"
  | "DISMISS"
  | "CANT_DETERMINE"
  | "BOX_18A";

export type CorrectValueSourceLiteral = "EMANIFEST" | "VENDOR" | "DOCUMENT" | "MANUAL";
export type ResolutionTargetLiteral = "VENDOR_DATA" | "EMANIFEST" | "DOCUMENT";

// ---------------------------------------------------------------------------
// "Other value" availability and value
// ---------------------------------------------------------------------------

/**
 * Fields a typed/document value cannot round-trip into a vendor write.
 * Mirrors MANUAL_UNSUPPORTED_FIELDS in vendorValueWrites.ts server-side;
 * keep both in sync manually.
 */
export const MANUAL_UNSUPPORTED_FIELDS: ReadonlySet<string> = new Set(["shippedDate"]);

/** Why "Other value" (a reviewer-typed value) can't be used, or null when it can: it needs a single targeted row on a supported field. */
export function otherValueDisabledReason(rows: readonly ResolveRow[]): string | null {
  if (rows.length !== 1) return "Only available for a single discrepancy";
  if (MANUAL_UNSUPPORTED_FIELDS.has(rows[0].field)) return "Can't be typed for this field";
  // The server takes a typed line number as an e-Manifest-only write, which leaves the line missing.
  if (isVendorLineAdditionRow(rows)) return "A missing line is added from e-Manifest, not typed";
  return null;
}

export const NO_DOCUMENT_REASON = "The document has no value for this field";

export const NON_POSITIVE_QUANTITY_REASON = "A quantity must be greater than 0";

/** Why a source can't be picked because a targeted quantity it holds is 0 or less (the server rejects it too), or null. */
export function nonPositiveQuantityReason<R extends { field: string }>(
  rows: readonly R[],
  valueOf: (row: R) => string | null | undefined
): string | null {
  const blocked = rows.some((r) => {
    const value = valueOf(r);
    return r.field === "quantity" && value != null && !isPositiveQuantity(value);
  });
  return blocked ? NON_POSITIVE_QUANTITY_REASON : null;
}

/** Why "Document's value" can't be picked, or null when every targeted row has a usable document value. */
export function documentValueDisabledReason(rows: readonly ResolveRow[]): string | null {
  if (rows.some((r) => r.manifestDocumentValue == null)) return NO_DOCUMENT_REASON;
  return nonPositiveQuantityReason(rows, (r) => r.manifestDocumentValue);
}

/** The stored-format string for whatever the reviewer typed/edited, given the field's shape. */
export function computeManualValue(field: string, text: string, codes: readonly string[]): string {
  if (isWasteCodeField(field)) {
    const normalized = codes.map((code) => code.trim().toUpperCase());
    return serializeWasteCodes(
      field === "stateWasteCodes" ? normalizeStateWasteCodes(normalized) : normalized
    );
  }
  if (field === "generatorId") return normalizeEpaId(text);
  if (field === "quantityUnitOfMeasureCode") return normalizeEpaWasteUnit(text) ?? text.trim();
  if (field === "quantity") {
    // "1,200" becomes "1200", so the preview, the document compare and the
    // mutation all read the number; unparseable text stays for manualValueError.
    const parsed = parseTypedQuantity(text);
    if (parsed.ok) return String(parsed.value);
  }
  return text.trim();
}

/**
 * True when the typed value is blank, or a waste-code list with no real codes
 * ("[]", or only sentinels like "NONE"). Saving either would clear the field.
 */
export function isEmptyManualValue(field: string, manualValue: string): boolean {
  if (manualValue.trim() === "") return true;
  return isWasteCodeField(field) && normalizeForCompare(field, manualValue) === "[]";
}

/** Why a typed value can't be saved, or null when it can. Quantity uses the same parser as the server. */
export function manualValueError(field: string | undefined, manualValue: string): string | null {
  if (isEmptyManualValue(field ?? "", manualValue)) return "Enter the correct value.";
  if (field === "quantityUnitOfMeasureCode" && !normalizeEpaWasteUnit(manualValue)) {
    return "Choose a valid manifest unit of measure.";
  }
  if (field === "generatorId" && !isValidEPAId(manualValue)) return EPA_ID_FORMAT_REASON;
  if (isWasteCodeField(field ?? "")) {
    const codes = parseWasteCodes(normalizeForCompare(field ?? "", manualValue));
    const invalid = invalidManifestCorrectionWasteCodes(field ?? "", codes);
    if (invalid.length > 0) return `Unrecognized waste codes: ${invalid.join(", ")}`;
  }
  if (field !== "quantity") return null;
  const parsed = parseTypedQuantity(manualValue);
  if (parsed.ok) return null;
  return parsed.reason === "NOT_POSITIVE" ? "Enter a quantity greater than 0." : "Enter a number.";
}

// ---------------------------------------------------------------------------
// Title / subtitle / document bar
// ---------------------------------------------------------------------------

/** "Resolve · <field label>", or "Resolve · N discrepancies" when the targeted rows span more than one field. */
export function formatResolveTitle(rows: readonly Pick<ResolveRow, "field">[]): string {
  if (rows.length === 0) return "Resolve";
  const fields = new Set(rows.map((r) => r.field));
  const label = fields.size === 1 ? getFieldLabel(rows[0].field) : `${rows.length} discrepancies`;
  return `Resolve · ${label}`;
}

/** "Manifest document, Box <n>: <value>" (or without the box clause), or null when the row has no document value. */
export function formatDocumentBarLabel(
  row: Pick<ResolveRow, "field" | "manifestDocumentValue">
): string | null {
  if (row.manifestDocumentValue == null) return null;
  const box = getEpaFormBox(row.field);
  const boxPart = box != null ? `, Box ${box}` : "";
  return `Manifest document${boxPart}: ${formatFieldValue(row.field, row.manifestDocumentValue)}`;
}

// ---------------------------------------------------------------------------
// correctValueSource for the chosen answer
// ---------------------------------------------------------------------------

export function correctValueSourceForAnswer(
  answer: Q1Answer,
  row: ResolveRow,
  manualValue: string
): CorrectValueSourceLiteral {
  if (answer === "EMANIFEST") return "EMANIFEST";
  if (answer === "VENDOR") return "VENDOR";
  if (answer === "DOCUMENT") return "DOCUMENT";
  return isSameAsDocument(row.field, manualValue, row.manifestDocumentValue)
    ? "DOCUMENT"
    : "MANUAL";
}

/** True for a value-sourced answer (DOCUMENT/MANUAL) — the only ones the reviewed-document rule, reopen trap, and MANUAL_UNSUPPORTED_FIELDS gate apply to. */
export function isValueSourcedAnswer(answer: Q1Answer): boolean {
  return answer === "SOMETHING_ELSE" || answer === "DOCUMENT";
}

/** The value each row is being declared correct at, for the given answer. EMANIFEST/VENDOR read it off the row itself; DOCUMENT reads the row's document value; SOMETHING_ELSE is one reviewer-typed value shared by the (single) targeted row. */
export function answerValueForRow(
  answer: Q1Answer,
  row: ResolveRow,
  manualValue: string | undefined
): string | null {
  switch (answer) {
    case "EMANIFEST":
      return row.eManifestValue ?? null;
    case "VENDOR":
      return row.vendorValue ?? null;
    case "DOCUMENT":
      return row.manifestDocumentValue ?? null;
    case "SOMETHING_ELSE":
      return manualValue ?? null;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Vendor-target eligibility (mirrors assertEligibleForVendorDataUpdate).
// ---------------------------------------------------------------------------

export interface VendorEligibility {
  eligibleIds: string[];
  skips: SkipGroup[];
}

/** The subset of ResolveRow this eligibility check needs — narrow enough that "apply to others" matching rows (which never carry eManifestValue/vendorValue) satisfy it too. */
export type VendorEligibilityRow = Pick<
  ResolveRow,
  "id" | "field" | "vendorAlignmentSuspect" | "vendorWasteLinesUnmatched"
>;

export function computeVendorEligibility(
  rows: readonly VendorEligibilityRow[],
  isValueSourced: boolean,
  valueDescription = "typed or document value"
): VendorEligibility {
  const eligibleIds: string[] = [];
  const skipCounts = new Map<string, number>();
  const addSkip = (reason: string) => skipCounts.set(reason, (skipCounts.get(reason) ?? 0) + 1);

  for (const row of rows) {
    if (!supportsVendorDataUpdate(row.field)) {
      addSkip(`${getFieldLabel(row.field)} can't be written to vendor data`);
    } else if (row.vendorAlignmentSuspect === true || row.vendorWasteLinesUnmatched === true) {
      // Mirrors assertEligibleForVendorDataUpdate: lines paired by number while
      // the counts differ could land the value on the wrong line.
      addSkip("vendor and e-Manifest waste lines can't be matched by line number");
    } else if (isValueSourced && MANUAL_UNSUPPORTED_FIELDS.has(row.field)) {
      addSkip(`vendor data can't take the ${valueDescription} for ${getFieldLabel(row.field)}`);
    } else {
      eligibleIds.push(row.id);
    }
  }

  const skips = [...skipCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);
  return { eligibleIds, skips };
}

// Target intersection: a row must pass every written target's gate (vendor,
// document) to be included in a call. e-Manifest never gates.

export type TargetEligibilityRow = Pick<
  ResolveRow,
  "id" | "field" | "vendorAlignmentSuspect" | "manifestDocumentValue"
>;

/**
 * The ids eligible for every written target, or null when neither vendor nor
 * document is written ("no filter"), distinct from an empty Set ("filter out
 * everything").
 */
export function eligibleIdsForWrittenTargets(
  rows: readonly TargetEligibilityRow[],
  isValueSourced: boolean,
  writesVendor: boolean,
  writesDocument: boolean
): Set<string> | null {
  if (!writesVendor && !writesDocument) return null;
  const vendorEligible = writesVendor
    ? new Set(computeVendorEligibility(rows, isValueSourced).eligibleIds)
    : null;
  const documentEligible = writesDocument
    ? new Set(computeDocumentEligibility(rows).eligibleIds)
    : null;
  const ids = rows
    .map((r) => r.id)
    .filter(
      (id) =>
        (vendorEligible === null || vendorEligible.has(id)) &&
        (documentEligible === null || documentEligible.has(id))
    );
  return new Set(ids);
}

// ---------------------------------------------------------------------------
// Per-source write defaults
// ---------------------------------------------------------------------------

/** True when at least one targeted row's current value differs from the declared-correct value. */
export function computeCopyDefault(
  rows: readonly ResolveRow[],
  getCurrent: (row: ResolveRow) => string | null | undefined,
  getAnswer: (row: ResolveRow) => string | null
): boolean {
  // Compare with normalizeForCompare: waste codes are an unordered set, so
  // ["331","219H"] already equals ["219H","331"].
  const normalized = (row: ResolveRow, value: string | null | undefined) =>
    value == null ? null : normalizeForCompare(row.field, value);
  return rows.some((row) => normalized(row, getCurrent(row)) !== normalized(row, getAnswer(row)));
}

/** Whether a source is written on save. When it is not, `reason` says why. */
export interface SourceWriteState {
  writes: boolean;
  reason?: string;
}

export const PICKED_VALUE_REASON = "This is the value you picked";
export const NO_ANSWER_REASON = "Pick the correct value first";

/** The Preview text when no value is written: before an answer, or for dismiss / block. */
export function inactiveResultText(answer: string | null): string {
  if (answer === "DISMISS") {
    return "No values change. The row becomes Non-Discrepancy. It reopens if a value changes on a later sync.";
  }
  if (answer === "CANT_DETERMINE") {
    return "No values change. Save asks for a reason, then marks this manifest as blocked.";
  }
  if (answer === "BOX_18A") {
    return "No values change. The row becomes Resolved with the Box 18a amount.";
  }
  return `${NO_ANSWER_REASON}.`;
}

/** The copy the reviewer picked as correct is never a write target, so it does not write and is disabled. */
function withSourceCopyDisabled(state: SourceWriteState, isSource: boolean): SourceWriteState {
  return isSource ? { writes: false, reason: PICKED_VALUE_REASON } : state;
}

/**
 * Whether vendor data is written: not (and disabled) when every row already
 * matches or none can take a vendor write, otherwise written (with a skip
 * reason shown when only some rows are eligible).
 */
export function computeVendorWriteState(
  rows: readonly ResolveRow[],
  isValueSourced: boolean,
  getAnswer: (row: ResolveRow) => string | null
): SourceWriteState {
  // The value writer can't create a line; picking e-Manifest adds it instead.
  if (!isValueSourced && isVendorLineAdditionRow(rows)) {
    const reason = vendorLineAdditionDisabledReason(rows[0]);
    return reason == null ? { writes: true } : { writes: false, reason };
  }
  if (!computeCopyDefault(rows, (r) => r.vendorValue, getAnswer)) {
    return { writes: false, reason: "Already correct" };
  }

  const { eligibleIds, skips } = computeVendorEligibility(rows, isValueSourced);
  if (eligibleIds.length === 0) {
    return { writes: false, reason: formatSkipReasons(skips, rows.length) };
  }
  return {
    writes: true,
    reason: skips.length > 0 ? formatSkipReasons(skips, rows.length) : undefined,
  };
}

/** Reasons joined for display; a count prefix only helps when there are several rows to count. */
function formatSkipReasons(skips: readonly SkipGroup[], totalRows: number): string {
  return skips.map((s) => (totalRows === 1 ? s.reason : `${s.count} ${s.reason}`)).join("; ");
}

/** Whether e-Manifest is written: no field-eligibility gate, only "Already correct". */
export function computeEManifestWriteState(
  rows: readonly ResolveRow[],
  getAnswer: (row: ResolveRow) => string | null
): SourceWriteState {
  if (!computeCopyDefault(rows, (r) => r.eManifestValue, getAnswer)) {
    return { writes: false, reason: "Already correct" };
  }
  return { writes: true };
}

/**
 * Whether the manifest document is written. With no document value on any
 * targeted row it is not written, with a reason instead of hidden.
 */
export function computeDocumentWriteState(
  rows: readonly ResolveRow[],
  getAnswer: (row: ResolveRow) => string | null
): SourceWriteState {
  if (rows.every((r) => r.manifestDocumentValue == null)) {
    return { writes: false, reason: NO_DOCUMENT_REASON };
  }

  if (!computeCopyDefault(rows, (r) => r.manifestDocumentValue, getAnswer)) {
    return { writes: false, reason: "Already correct" };
  }

  const { eligibleIds, skips } = computeDocumentEligibility(rows);
  if (eligibleIds.length === 0) {
    return { writes: false, reason: formatSkipReasons(skips, rows.length) };
  }
  return {
    writes: true,
    reason: skips.length > 0 ? formatSkipReasons(skips, rows.length) : undefined,
  };
}

/** The value shown after "currently" — the shared value when every targeted row agrees, else null (caller falls back to generic copy). */
export function sharedRowValue(
  rows: readonly ResolveRow[],
  getValue: (row: ResolveRow) => string | null | undefined
): string | null {
  if (rows.length === 0) return null;
  const [first, ...rest] = rows.map((r) => getValue(r) ?? null);
  return rest.every((v) => v === first) ? first : null;
}

// Client-side mirrors of server validations, single-row only (both apply
// only to value-sourced answers).

// ---------------------------------------------------------------------------
// Save gating
// ---------------------------------------------------------------------------

export function applyToFromWriteStates(
  writesVendor: boolean,
  writesEManifest: boolean,
  writesDocument: boolean
): ResolutionTargetLiteral[] {
  const applyTo: ResolutionTargetLiteral[] = [];
  if (writesVendor) applyTo.push("VENDOR_DATA");
  if (writesEManifest) applyTo.push("EMANIFEST");
  if (writesDocument) applyTo.push("DOCUMENT");
  return applyTo;
}

/** True for an answer that shows the Preview table: every answer but dismiss/block, and only once one is picked. */
export function answerShowsPreview(answer: Q1Answer | null): boolean {
  return (
    answer != null && answer !== "DISMISS" && answer !== "CANT_DETERMINE" && answer !== "BOX_18A"
  );
}

/**
 * Whether Save is disabled, given the chosen answer and the Preview's own disabled
 * reason for value-sourced answers. Kept out of the component so its render
 * body doesn't grow another branch per answer kind.
 */
export function computeDialogSaveDisabled(opts: {
  loading: boolean;
  answer: Q1Answer | null;
  box18aAmountValid?: boolean;
  dismissEligibleCount: number;
  hasCantDetermineOption: boolean;
  showsPreview: boolean;
  saveDisabledReason: string | null;
}): boolean {
  const {
    loading,
    answer,
    box18aAmountValid,
    dismissEligibleCount,
    hasCantDetermineOption,
    showsPreview,
    saveDisabledReason,
  } = opts;
  if (loading || !answer) return true;
  if (answer === "BOX_18A") return !box18aAmountValid;
  if (answer === "DISMISS") return dismissEligibleCount === 0;
  if (answer === "CANT_DETERMINE") return !hasCantDetermineOption;
  return showsPreview && saveDisabledReason != null;
}

/** Staff acting on exactly one row, with a matching-finding query to drive. */
export function computeCanApplyToOthers(
  isStaff: boolean,
  totalCount: number,
  singleDiscrepancyId: string | undefined
): boolean {
  return isStaff && totalCount === 1 && singleDiscrepancyId != null;
}

/** The "apply to others" control renders once matches are loaded, and never for a value-sourced answer (a typed or document value is specific to its own row) or a vendor line addition (single-row only). */
export function computeShowApplyToOthers(
  canApplyToOthers: boolean,
  matchingLoading: boolean,
  matchingCount: number,
  answer: Q1Answer | null,
  rows: readonly Pick<ResolveRow, "field">[]
): boolean {
  return (
    canApplyToOthers &&
    !matchingLoading &&
    matchingCount > 0 &&
    !(answer != null && (isValueSourcedAnswer(answer) || answer === "BOX_18A")) &&
    !isVendorLineAdditionSave(answer, rows)
  );
}

/** The document bar's "View document" link needs both a callback to open the preview and a document on this row. */
export function computeShowOpenDocumentLink(
  hasOnViewDocument: boolean,
  sourceDocumentId: string | null | undefined
): boolean {
  return hasOnViewDocument && sourceDocumentId != null;
}

/**
 * True when at least one targeted row is no longer UNRESOLVED. Shared gate
 * behind both the pre-answer radio disable and the post-answer Save
 * disable.
 */
function hasNonUnresolvedRow(rows: readonly ResolveRow[]): boolean {
  return rows.some((r) => r.status !== "UNRESOLVED");
}

/**
 * Reason Save is disabled for a value-sourced answer, or null when ready to
 * send: not every row is UNRESOLVED, SOMETHING_ELSE with an empty value, or
 * no target written. Disagreeing copies are warnings, not blocks.
 */
export function computeSaveDisabledReason(opts: {
  rows: readonly ResolveRow[];
  answer: Q1Answer;
  manualValue: string;
  writesVendor: boolean;
  writesEManifest: boolean;
  writesDocument: boolean;
}): string | null {
  const { rows, answer, manualValue, writesVendor, writesEManifest, writesDocument } = opts;

  if (hasNonUnresolvedRow(rows)) {
    return "This row is no longer Pending Review. Close and reopen the dialog.";
  }
  const manualReason =
    answer === "SOMETHING_ELSE" ? manualValueError(rows[0]?.field, manualValue) : null;
  if (manualReason) return manualReason;
  if (!writesVendor && !writesEManifest && !writesDocument) {
    return (
      lineAdditionReason(answer, rows) ??
      "Nothing here can be updated. Pick another answer or mark it a Non-Discrepancy."
    );
  }
  return null;
}

function lineAdditionReason(answer: Q1Answer, rows: readonly ResolveRow[]): string | null {
  return isVendorLineAdditionSave(answer, rows) ? vendorLineAdditionDisabledReason(rows[0]) : null;
}

/**
 * Non-blocking warnings for a single-row value answer: one line per copy that
 * still disagrees after the writes, saying whether the row stays
 * Pending Review or goes back to it once e-Manifest is corrected.
 * Multi-row selections get none, since copies can differ per row.
 */
export function computeSaveWarnings(opts: {
  rows: readonly ResolveRow[];
  answerValue: string | null;
  writesVendor: boolean;
  writesEManifest: boolean;
  writesDocument: boolean;
}): string[] {
  const { rows, answerValue, ...writeFlags } = opts;
  if (rows.length !== 1 || answerValue == null) return [];
  const row = rows[0];
  if (isEmptyManualValue(row.field, answerValue)) return [];
  return copyWarningMessages({ row, answerValue, ...writeFlags });
}

/**
 * A row that's no longer UNRESOLVED must not offer a value answer
 * (EMANIFEST/VENDOR/DOCUMENT/SOMETHING_ELSE) at all, not just fail Save after the
 * fact. DISMISS/CANT_DETERMINE are unaffected.
 */
export function valueAnswersDisabledReason(rows: readonly ResolveRow[]): string | null {
  const blocking = rows.find((r) => r.status !== "UNRESOLVED");
  if (!blocking) return null;
  const label = getStatusLabel(getDiscrepancyStatus(blocking));
  return `This row is ${label}. Undo or reopen it to pick a different value.`;
}

/** Why "Mark as non-discrepancy" can't be picked, from the parent's dismiss eligibility (e.g. a row pending a manual resolution), or null when it can. */
export function dismissDisabledReason(option: {
  eligibleIds: readonly string[];
  skipSummary?: string;
}): string | null {
  if (option.eligibleIds.length > 0) return null;
  return topSkipFromSummary(option.skipSummary, 1)?.reason ?? "not eligible";
}

export interface PreviewState {
  vendorState: SourceWriteState | null;
  eManifestState: SourceWriteState | null;
  documentState: SourceWriteState | null;
  showsPreview: boolean;
  saveDisabledReason: string | null;
  saveWarnings: string[];
}

/**
 * Folds the per-source write states, the summary line, and the inline
 * validation message into one call, so the dialog's render body holds none of
 * this branching.
 */
export function derivePreviewState(opts: {
  rows: readonly ResolveRow[];
  answer: Q1Answer | null;
  manualValue: string;
  vendorDefault: SourceWriteState | null;
  eManifestDefault: SourceWriteState | null;
  documentDefault: SourceWriteState | null;
  getAnswerValue: (row: ResolveRow) => string | null;
}): PreviewState {
  const {
    rows,
    answer,
    manualValue,
    vendorDefault,
    eManifestDefault,
    documentDefault,
    getAnswerValue,
  } = opts;

  const vendorState = vendorDefault
    ? withSourceCopyDisabled(vendorDefault, answer === "VENDOR")
    : null;
  const eManifestState = eManifestDefault
    ? withSourceCopyDisabled(eManifestDefault, answer === "EMANIFEST")
    : null;
  // The document row is always rendered (disabled when it can't apply), but
  // only vendor/eManifest gate whether the Preview renders at all.
  const documentState = documentDefault
    ? withSourceCopyDisabled(documentDefault, answer === "DOCUMENT")
    : null;
  const showsPreview = answerShowsPreview(answer) && vendorState != null && eManifestState != null;

  if (!showsPreview || !vendorState || !eManifestState || !answer) {
    return {
      vendorState,
      eManifestState,
      documentState,
      showsPreview,
      saveDisabledReason: null,
      saveWarnings: [],
    };
  }

  const writesDocument = documentState?.writes ?? false;
  const saveDisabledReason = computeSaveDisabledReason({
    rows,
    answer,
    manualValue,
    writesVendor: vendorState.writes,
    writesEManifest: eManifestState.writes,
    writesDocument,
  });
  const saveWarnings = computeSaveWarnings({
    rows,
    answerValue: answer === "SOMETHING_ELSE" ? manualValue : getAnswerValue(rows[0]),
    writesVendor: vendorState.writes,
    writesEManifest: eManifestState.writes,
    writesDocument,
  });
  return {
    vendorState,
    eManifestState,
    documentState,
    showsPreview,
    saveDisabledReason,
    saveWarnings,
  };
}

// ---------------------------------------------------------------------------
// Mutation variables
// ---------------------------------------------------------------------------

export interface ResolveWithValueInput {
  discrepancyIds: string[];
  correctValueSource: CorrectValueSourceLiteral;
  correctValue?: string;
  applyTo: ResolutionTargetLiteral[];
  note?: string | null;
}

/**
 * The single resolveDiscrepancies call for a value-sourced answer. When
 * only some rows are eligible for every written target, the call is
 * restricted to that eligible subset; ineligible rows stay unresolved.
 */
export function buildResolveWithValueInput(opts: {
  rows: readonly ResolveRow[];
  answer: Q1Answer;
  manualValue: string;
  writesVendor: boolean;
  writesEManifest: boolean;
  writesDocument: boolean;
  note: string | null;
}): ResolveWithValueInput {
  const { rows, answer, manualValue, writesVendor, writesEManifest, writesDocument, note } = opts;
  const isValueSourced = isValueSourcedAnswer(answer);
  const applyTo = applyToFromWriteStates(writesVendor, writesEManifest, writesDocument);

  const eligibleIds = eligibleIdsForWrittenTargets(
    rows,
    isValueSourced,
    writesVendor,
    writesDocument
  );
  const targetRows = eligibleIds ? rows.filter((r) => eligibleIds.has(r.id)) : rows;

  const source = correctValueSourceForAnswer(answer, rows[0], manualValue);
  return {
    discrepancyIds: targetRows.map((r) => r.id),
    correctValueSource: source,
    correctValue: source === "MANUAL" ? manualValue : undefined,
    applyTo,
    note: note || undefined,
  };
}
